import React, { useState, useEffect } from 'react';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';

interface ChecklistItem {
    id: number;
    text: string;
    status: 'Okay' | 'Not Okay' | '';
    remarks: string;
}

interface ChecklistSection {
    title: string;
    items: ChecklistItem[];
}

const initialChecklistData: ChecklistSection[] = [
    {
        title: "Personal Hygiene",
        items: [
            { id: 1, text: "Protective clothing/head covering worn properly", status: '', remarks: '' },
            { id: 2, text: "Freedom from skin disease/wounds", status: '', remarks: '' },
            { id: 3, text: "No small articles carried in shirt pockets", status: '', remarks: '' },
            { id: 4, text: "Hand wash & IPA sanitization followed", status: '', remarks: '' },
            { id: 5, text: "No jewellery/glass bangles/flowers/bindi on shop floor", status: '', remarks: '' },
        ]
    },
    {
        title: "Mixing",
        items: [
            { id: 6, text: "Material identification maintained", status: '', remarks: '' },
            { id: 7, text: "Sieving before use done properly", status: '', remarks: '' },
            { id: 8, text: "Covered pre-weighed ingredients stored safely", status: '', remarks: '' },
            { id: 9, text: "Separate stitching-thread disposal maintained", status: '', remarks: '' },
            { id: 10, text: "Sieve & magnet condition checked & OK", status: '', remarks: '' },
            { id: 11, text: "Sieve collection disposal handled properly", status: '', remarks: '' },
            { id: 12, text: "Old dough storage & traceability maintained", status: '', remarks: '' },
            { id: 13, text: "Covered/cleaned storage containers used", status: '', remarks: '' },
            { id: 14, text: "Ingredient traceability record maintained", status: '', remarks: '' },
            { id: 15, text: "Strip curtain cleaning followed", status: '', remarks: '' },
            { id: 16, text: "Clean handling containers used", status: '', remarks: '' },
            { id: 17, text: "Mixer cleaned top & sides", status: '', remarks: '' },
            { id: 18, text: "Premixing/mixing floor cleaned", status: '', remarks: '' },
            { id: 19, text: "Broken-grinding section cleaned & trays identified", status: '', remarks: '' },
            { id: 20, text: 'Defectives kept 18" from wall', status: '', remarks: '' },
            { id: 21, text: "Defectives identified by variety", status: '', remarks: '' },
            { id: 22, text: "Biscuits sorted before grinding (overbaked/foreign matter removed)", status: '', remarks: '' },
            { id: 23, text: "Dust collection tray condition checked & OK", status: '', remarks: '' },
            { id: 24, text: "Metal detector effectiveness check done", status: '', remarks: '' },
            { id: 25, text: "Defective biscuits (usable/unusable) segregated at oven end", status: '', remarks: '' },
            { id: 26, text: "Windows/ventilators meshed & cleaned", status: '', remarks: '' },
            { id: 27, text: "Food-handling containers undamaged", status: '', remarks: '' },
            { id: 28, text: "Band cleaning/condition checked & OK", status: '', remarks: '' },
            { id: 29, text: "Dough collected & transferred safely", status: '', remarks: '' },
            { id: 30, text: "No water stagnation in mixing area", status: '', remarks: '' },
        ]
    },
    {
        title: "Packing",
        items: [
            { id: 31, text: "CBB/laminate storage on pallets away from wall", status: '', remarks: '' },
            { id: 32, text: "Cooling/packing conveyors in good condition", status: '', remarks: '' },
            { id: 33, text: "Rollers/scrapers/catch trays clean", status: '', remarks: '' },
            { id: 34, text: "No rejected rolls kept in packing", status: '', remarks: '' },
            { id: 35, text: "Catch trays under machines clean", status: '', remarks: '' },
            { id: 36, text: "Biscuit trays moved on trolleys (not dragged)", status: '', remarks: '' },
            { id: 37, text: "Defective biscuits passed through magnet before grinding", status: '', remarks: '' },
            { id: 38, text: "Tray-packed biscuits not touching tray above", status: '', remarks: '' },
            { id: 39, text: "Cleaning schedules followed", status: '', remarks: '' },
            { id: 40, text: "Day/shift/production-center code legible on pack", status: '', remarks: '' },
            { id: 41, text: "Empty CBB inspected before filling", status: '', remarks: '' },
            { id: 42, text: "No staple pins/rubber bands/threads allowed", status: '', remarks: '' },
            { id: 43, text: "Metal detector checked per line", status: '', remarks: '' },
            { id: 44, text: "Window condition OK", status: '', remarks: '' },
            { id: 45, text: "Floor clean", status: '', remarks: '' },
            { id: 46, text: "Biscuit trays periodically cleaned", status: '', remarks: '' },
        ]
    }
];

const GMPChecklist: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const [sections, setSections] = useState<ChecklistSection[]>(initialChecklistData);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasLoadedRef = React.useRef<string | null>(null);

    // Resume logic: pre-fill items from Dataverse if resuming a paused tour
    useEffect(() => {
        const loadResumedData = async () => {
            if (state.isPaused && state.currentTourId && hasLoadedRef.current !== state.currentTourId) {
                hasLoadedRef.current = state.currentTourId;
                try {
                    const items = await foodSafetyService.getChecklistItems(state.currentTourId);
                    if (items && items.length > 0) {
                        setSections(prev => prev.map(section => ({
                            ...section,
                            items: section.items.map(item => {
                                const match = items.find((d: any) => d.cr953_food_safety_criteria === item.text);
                                if (match) {
                                    return {
                                        ...item,
                                        status: (match.cr953_food_safety_status || '') as 'Okay' | 'Not Okay' | '',
                                        remarks: match.cr953_food_safety_defectremarks || ''
                                    };
                                }
                                return item;
                            })
                        })));
                    }
                } catch (error) {
                    console.error("Failed to load paused GMP items", error);
                }
            }
        };
        loadResumedData();
    }, [state.isPaused, state.currentTourId]);

    const handleStatusChange = (sectionIndex: number, itemIndex: number, status: 'Okay' | 'Not Okay') => {
        const newSections = [...sections];
        newSections[sectionIndex].items[itemIndex].status = status;
        if (status === 'Okay') {
            newSections[sectionIndex].items[itemIndex].remarks = '';
        }
        setSections(newSections);
    };

    const handleRemarksChange = (sectionIndex: number, itemIndex: number, remarks: string) => {
        const newSections = [...sections];
        newSections[sectionIndex].items[itemIndex].remarks = remarks;
        setSections(newSections);
    };

    const calculateScores = () => {
        let totalOkay = 0;
        let packingOkay = 0;
        let totalItems = 0;
        const packingSection = sections.find(s => s.title === 'Packing');
        const packingTotal = packingSection ? packingSection.items.length : 16;

        sections.forEach(sec => {
            sec.items.forEach(item => {
                totalItems++;
                if (item.status === 'Okay') totalOkay++;
                if (sec.title === 'Packing' && item.status === 'Okay') packingOkay++;
            });
        });

        const gmpScore = totalItems > 0 ? (totalOkay / totalItems) * 100 : 0;
        const packingScore = packingTotal > 0 ? (packingOkay / packingTotal) * 100 : 0;
        const totalAnswered = sections.reduce((sum, s) => sum + s.items.filter(i => i.status !== '').length, 0);

        return { gmpScore: gmpScore.toFixed(2), packingScore: packingScore.toFixed(2), totalAnswered, totalItems };
    };

    const { gmpScore, packingScore, totalAnswered, totalItems } = calculateScores();

    const saveRows = async () => {
        if (!state.currentTourId || !state.sessionData) return;

        await foodSafetyService.cleanChecklistItems(state.currentTourId);

        for (const section of sections) {
            for (const item of section.items) {
                if (!item.status) continue; // Skip unanswered items on pause
                const rowPayload = {
                    cr953_food_safety_title: `GMP - ${state.sessionData.site} - ${state.sessionData.line} - ${section.title}`,
                    "cr953_food_safety_tourid@odata.bind": `/cr3ea_prod_rajpura_quality_tours(${state.currentTourId})`,
                    cr953_food_safety_checklisttype: state.selectedChecklistType,
                    cr953_food_safety_manufacturingsite: state.sessionData.site,
                    cr953_food_safety_line: state.sessionData.line,
                    cr953_food_safety_qaexecutive: state.sessionData.qaExecutive,
                    cr953_food_safety_productionincharge: state.sessionData.productionIncharge,
                    cr953_food_safety_area: section.title,
                    cr953_food_safety_criteria: item.text,
                    cr953_food_safety_cycle: state.sessionData.cycle,
                    cr953_food_safety_status: item.status,
                    cr953_food_safety_defectremarks: item.remarks,
                    cr953_food_safety_date: state.sessionData.tourStartDate,
                };
                await foodSafetyService.saveChecklistRow(rowPayload);
            }
        }
    };

    const handlePause = async () => {
        if (!state.currentTourId) return;
        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await saveRows();
            await foodSafetyService.saveSession({
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_status: "InProgress-paused"
            });
            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.WELCOME });
        } catch (error) {
            console.error("Error pausing GMP Checklist:", error);
            alert("Failed to pause checklist.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const handleSubmit = async () => {
        const allAnswered = sections.every(sec => sec.items.every(item => item.status !== ''));
        if (!allAnswered) {
            alert("Please provide a status for all checklist items.");
            return;
        }

        const missingRemarks = sections.some(sec => sec.items.some(item => item.status === 'Not Okay' && !item.remarks.trim()));
        if (missingRemarks) {
            alert("Please provide remarks for all 'Not Okay' items.");
            return;
        }

        if (!state.currentTourId || !state.sessionData) {
            alert("Session missing. Please start again.");
            return;
        }

        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            await saveRows();

            const passFail = parseFloat(gmpScore) >= 80 ? "Pass" : "Fail";
            await foodSafetyService.saveSession({
                cr3ea_prod_rajpura_quality_tourid: state.currentTourId,
                cr3ea_overall_score: `${gmpScore}%`,
                cr3ea_checklist_result: passFail,
                cr3ea_status: "Submitted",
                cr3ea_tourcompletiondate: new Date().toISOString()
            });

            dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.SUMMARY });

        } catch (error: any) {
            console.error("Error submitting GMP Checklist:", error);
            alert("Failed to submit checklist. Please check your connection.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div>
            {/* Stepper Workflow Bar */}
            <div style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        1. Request Info
                    </span>
                    <span style={{ color: '#94a3b8' }}>➔</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#80cbcf', color: '#ffffff', padding: '4px 12px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                        2. GMP Checklist
                    </span>
                </div>
            </div>

            {/* Sticky Progress Banner */}
            <div className="card shadow-sm border-0 mb-4 sticky-top" style={{ top: '10px', zIndex: 1000 }}>
                <div className="card-body py-2 px-4 d-flex flex-wrap justify-content-between align-items-center bg-light rounded gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-secondary">
                            Progress: <span className="text-primary">{totalAnswered}/{totalItems}</span>
                        </span>
                        <span className="fw-bold text-secondary">
                            GMP Score: <span className="text-primary">{gmpScore}%</span>
                        </span>
                        <span className={`badge ${parseFloat(gmpScore) >= 80 ? 'bg-success' : 'bg-danger'}`}>
                            {parseFloat(gmpScore) >= 80 ? 'Pass' : 'Fail'}
                        </span>
                    </div>
                    <div>
                        <button className="btn btn-sm btn-outline-warning fw-bold me-2" onClick={handlePause} disabled={isSubmitting}>
                            <i className="bi bi-pause-circle me-1"></i>Pause Tour
                        </button>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                    <h5 className="card-title mb-0 fw-bold">GMP Checklist</h5>
                    <div className="d-flex gap-2">
                        <span className="badge bg-info text-dark px-3 py-2 fs-6">Packing Score: {packingScore}%</span>
                        <span className="badge bg-primary px-3 py-2 fs-6">GMP Score: {gmpScore}%</span>
                    </div>
                </div>
                <div className="card-body p-4">
                    {sections.map((section, sIndex) => (
                        <div key={section.title} className="mb-5">
                            <h6 className="fw-bold text-primary mb-3 border-bottom pb-2">{section.title}</h6>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '5%' }}>Sr.</th>
                                            <th style={{ width: '45%' }}>Criteria</th>
                                            <th style={{ width: '25%' }}>Status</th>
                                            <th style={{ width: '25%' }}>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {section.items.map((item, iIndex) => (
                                            <tr key={item.id} className={item.status === 'Not Okay' ? 'table-warning' : ''}>
                                                <td className="text-center">{item.id}</td>
                                                <td>{item.text}</td>
                                                <td>
                                                    <div className="d-flex gap-3">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name={`status-${item.id}`}
                                                                id={`okay-${item.id}`}
                                                                checked={item.status === 'Okay'}
                                                                onChange={() => handleStatusChange(sIndex, iIndex, 'Okay')}
                                                            />
                                                            <label className="form-check-label text-success fw-semibold" htmlFor={`okay-${item.id}`}>
                                                                Okay
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name={`status-${item.id}`}
                                                                id={`notokay-${item.id}`}
                                                                checked={item.status === 'Not Okay'}
                                                                onChange={() => handleStatusChange(sIndex, iIndex, 'Not Okay')}
                                                            />
                                                            <label className="form-check-label text-danger fw-semibold" htmlFor={`notokay-${item.id}`}>
                                                                Not Okay
                                                            </label>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {item.status === 'Not Okay' ? (
                                                        <input
                                                            type="text"
                                                            className={`form-control ${item.status === 'Not Okay' && !item.remarks.trim() ? 'is-invalid' : ''}`}
                                                            placeholder="Required remarks..."
                                                            value={item.remarks}
                                                            onChange={(e) => handleRemarksChange(sIndex, iIndex, e.target.value)}
                                                        />
                                                    ) : (
                                                        <span className="text-muted fst-italic">N/A</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}

                    <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                        <button
                            className="btn btn-success px-4 btn-lg"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit GMP Checklist'} <i className="bi bi-check-circle ms-2"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GMPChecklist;
